import { useNavigate } from "react-router-dom";
import useFetch from "@/lib/useFetch";
import { PageHeader, Loading, ErrorState, EmptyState, Avatar } from "@/components/common";
import { brl, pct, num, plural } from "@/lib/format";
import { ClipboardList, TrendingUp, TrendingDown, Minus } from "@/components/icons";

const achTone = (a) => (a >= 100 ? "text-ok" : a >= 70 ? "text-warn" : "text-bad");
const barTone = (a) => (a >= 100 ? "bg-ok" : a >= 70 ? "bg-warn" : "bg-bad");

function Variacao({ v, novo }) {
  if (v == null) {
    if (novo) return <span className="inline-flex items-center gap-1 text-xs font-medium text-ok">Novo</span>;
    return <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Minus className="w-3 h-3" /> —</span>;
  }
  const up = v >= 0;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${up ? "text-ok" : "text-bad"}`}>
      <Icon className="w-3.5 h-3.5" /> {up ? "+" : "−"}{pct(Math.abs(v), 1)}
    </span>
  );
}

export default function ResumoExecutivo() {
  const navigate = useNavigate();
  const { data, loading, error, reload } = useFetch("/resumo-executivo");
  if (loading) return <Loading />;
  if (error) return <ErrorState onRetry={reload} />;

  const vendedores = data?.vendedores || [];
  const podeComparar = !!data?.pode_comparar;
  const subtitle = podeComparar
    ? `${data?.periodo} · ${plural(vendedores.length, "vendedor", "vendedores")} — comparação restrita à gestão`
    : `${data?.periodo} · seus resultados`;

  return (
    <div>
      <PageHeader title="Resumo executivo" subtitle={subtitle} />

      {vendedores.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Sem vendedores no seu escopo" subtitle="Vendedores vinculados aparecem aqui para acompanhamento." />
      ) : (
        <>
          {(data?.cota_oficial_por_gestor || []).length > 0 && (
            <div className="hidden lg:block rounded-2xl border border-black/5 bg-card shadow-[0_2px_10px_rgb(0,0,0,0.03)] overflow-x-auto mb-4" data-testid="resumo-cota-oficial">
              <div className="px-4 pt-4 pb-2">
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Cota real por Gestor</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Acumulado no ano até {data?.periodo} · gestor + vendedores sob ele.</p>
              </div>
              <table className="w-full text-sm min-w-[920px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-black/5">
                    <th className="px-4 py-3 font-semibold">Gestor</th>
                    <th className="px-4 py-3 font-semibold text-right" title="Cota anual definida pela diretoria">Cota real</th>
                    <th className="px-4 py-3 font-semibold text-right" title="Soma das cotas dos vendedores do gestor (anual)">Meta distribuída</th>
                    <th className="px-4 py-3 font-semibold text-right" title="(Meta distribuída ÷ Cota real − 1) × 100">Gordura %</th>
                    <th className="px-4 py-3 font-semibold text-right" title="Líquido Sankhya acumulado no ano">Faturado</th>
                    <th className="px-4 py-3 font-semibold text-right">% da cota real</th>
                    <th className="px-4 py-3 font-semibold text-right">% da meta distribuída</th>
                  </tr>
                </thead>
                <tbody>
                  {data.cota_oficial_por_gestor.map((r) => {
                    const MESES_LONG = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
                    const mesLabel = MESES_LONG[(r.mes_sel || 1) - 1];
                    const sgn = (v) => v == null ? "—" : `${v >= 0 ? "+" : ""}${pct(v, 1)}`;
                    const p = (v) => v == null ? "—" : pct(v, 1);
                    return [
                      (
                        <tr key={`${r.gestor_id}-mes`} className="border-b border-black/5 hover:bg-slate-50" data-testid={`resumo-cota-row-${r.gestor_id}-mes`}>
                          <td className="px-4 py-3">
                            <div className="font-medium text-navy">{r.nome}</div>
                            <div className="text-[11px] text-muted-foreground">{r.cargo} · {mesLabel}</div>
                          </td>
                          <td className="px-4 py-3 text-right text-navy">{brl(r.cota_mes)}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{brl(r.meta_mes)}</td>
                          <td className="px-4 py-3 text-right text-navy">{sgn(r.gordura_mes_pct)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-navy">{brl(r.faturado_mes)}</td>
                          <td className={`px-4 py-3 text-right font-semibold ${r.pct_cota_oficial_mes == null ? "text-muted-foreground" : achTone(r.pct_cota_oficial_mes)}`}>{p(r.pct_cota_oficial_mes)}</td>
                          <td className={`px-4 py-3 text-right font-semibold ${r.pct_meta_distribuida_mes == null ? "text-muted-foreground" : achTone(r.pct_meta_distribuida_mes)}`}>{p(r.pct_meta_distribuida_mes)}</td>
                        </tr>
                      ),
                      (
                        <tr key={`${r.gestor_id}-acc`} className="border-b border-black/5 last:border-0 bg-slate-50/40" data-testid={`resumo-cota-row-${r.gestor_id}`}>
                          <td className="px-4 py-3">
                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Acumulado até {mesLabel}</div>
                          </td>
                          <td className="px-4 py-3 text-right text-navy">{brl(r.cota_oficial_ytd)}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{brl(r.meta_distribuida_ytd)}</td>
                          <td className="px-4 py-3 text-right text-navy">{sgn(r.gordura_pct)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-navy">{brl(r.faturado_ytd)}</td>
                          <td className={`px-4 py-3 text-right font-semibold ${r.pct_cota_oficial == null ? "text-muted-foreground" : achTone(r.pct_cota_oficial)}`}>{p(r.pct_cota_oficial)}</td>
                          <td className={`px-4 py-3 text-right font-semibold ${r.pct_meta_distribuida == null ? "text-muted-foreground" : achTone(r.pct_meta_distribuida)}`}>{p(r.pct_meta_distribuida)}</td>
                        </tr>
                      ),
                    ];
                  })}
                </tbody>
              </table>
            </div>
          )}
          {(data?.cota_oficial_por_gestor || []).length > 0 && (
            <div className="lg:hidden space-y-3 mb-4" data-testid="resumo-cota-oficial-mobile">
              <div>
                <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Cota real por Gestor</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Acumulado no ano · gestor + vendedores sob ele.</p>
              </div>
              {data.cota_oficial_por_gestor.map((r) => (
                <div key={r.gestor_id} className="bg-card rounded-2xl border border-black/5 p-4 shadow-[0_2px_10px_rgb(0,0,0,0.03)]" data-testid={`resumo-cota-card-${r.gestor_id}`}>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <div className="font-semibold text-navy truncate">{r.nome}</div>
                      <div className="text-[11px] text-muted-foreground">{r.cargo}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Faturado</div>
                      <div className="text-sm font-semibold text-navy">{brl(r.faturado_ytd)}</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                    <div><div className="text-xs font-semibold text-navy">{brl(r.cota_oficial_ytd)}</div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Cota real</div></div>
                    <div><div className="text-xs font-semibold text-slate-600">{brl(r.meta_distribuida_ytd)}</div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Meta dist.</div></div>
                    <div><div className="text-xs font-semibold text-navy">{r.gordura_pct == null ? "—" : `${r.gordura_pct >= 0 ? "+" : ""}${pct(r.gordura_pct, 1)}`}</div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Gordura</div></div>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-black/5 text-xs">
                    <span className={`font-medium ${r.pct_cota_oficial == null ? "text-muted-foreground" : achTone(r.pct_cota_oficial)}`}>{r.pct_cota_oficial == null ? "—" : pct(r.pct_cota_oficial, 1)} da cota</span>
                    <span className={`font-medium ${r.pct_meta_distribuida == null ? "text-muted-foreground" : achTone(r.pct_meta_distribuida)}`}>{r.pct_meta_distribuida == null ? "—" : pct(r.pct_meta_distribuida, 1)} da meta</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Desktop: tabela enxuta com rolagem horizontal quando necessário */}
          <div className="hidden lg:block rounded-2xl border border-black/5 bg-card shadow-[0_2px_10px_rgb(0,0,0,0.03)] overflow-x-auto pipeline-scroll" data-testid="resumo-tabela">
            <table className="w-full text-sm min-w-[920px]">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground border-b border-black/5">
                  <th className="px-4 py-3 font-semibold">Vendedor</th>
                  <th className="px-4 py-3 font-semibold text-right">Meta</th>
                  <th className="px-4 py-3 font-semibold text-right">Realizado</th>
                  <th className="px-4 py-3 font-semibold text-right">% Ating.</th>
                  <th className="px-4 py-3 font-semibold text-right">Oportunidades</th>
                  <th className="px-4 py-3 font-semibold text-right">Previsão ponderada</th>
                  <th className="px-4 py-3 font-semibold text-center">Ganhas</th>
                  <th className="px-4 py-3 font-semibold text-center">Perdidas</th>
                  <th className="px-4 py-3 font-semibold text-center">Requer atenção</th>
                  <th className="px-4 py-3 font-semibold text-right">vs {data?.periodo_anterior}</th>
                </tr>
              </thead>
              <tbody>
                {vendedores.map((v) => (
                  <tr key={v.id} onClick={() => navigate(`/membro/${v.id}`)} data-testid={`resumo-row-${v.id}`}
                    className="border-b border-black/5 last:border-0 hover:bg-slate-50 cursor-pointer transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar name={v.nome} src={v.avatar} size={34} />
                        <span className="font-medium text-navy">{v.nome}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-muted-foreground">{brl(v.meta)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-navy">{brl(v.realizado)}</td>
                    <td className={`px-4 py-3 text-right font-semibold ${achTone(v.atingimento)}`}>{pct(v.atingimento, 0)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="font-medium text-navy">{num(v.total_oportunidades)}</div>
                      <div className="text-xs text-muted-foreground">{brl(v.total_valor)}</div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-brand">{brl(v.previsao_ponderada)}</td>
                    <td className="px-4 py-3 text-center font-semibold text-ok">{num(v.ganhas)}</td>
                    <td className="px-4 py-3 text-center font-semibold text-bad">{num(v.perdidas)}</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`inline-flex items-center justify-center min-w-6 h-6 px-2 rounded-full text-xs font-semibold ${v.requer_atencao > 0 ? "bg-warn/15 text-warn" : "bg-slate-100 text-slate-500"}`}>{num(v.requer_atencao)}</span>
                    </td>
                    <td className="px-4 py-3 text-right"><Variacao v={v.variacao_realizado} novo={v.variacao_realizado_novo} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: cartão compacto por vendedor */}
          <div className="lg:hidden space-y-3" data-testid="resumo-mobile">
            {vendedores.map((v) => (
              <div key={v.id} onClick={() => navigate(`/membro/${v.id}`)} data-testid={`resumo-card-${v.id}`}
                className="bg-card rounded-2xl border border-black/5 p-4 shadow-[0_2px_10px_rgb(0,0,0,0.03)] active:bg-slate-50">
                <div className="flex items-center gap-3">
                  <Avatar name={v.nome} src={v.avatar} size={40} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-navy truncate">{v.nome}</div>
                    <div className="text-xs text-muted-foreground">{brl(v.realizado)} / {brl(v.meta)}</div>
                  </div>
                  <span className={`font-semibold ${achTone(v.atingimento)}`}>{pct(v.atingimento, 0)}</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full mt-3 overflow-hidden">
                  <div className={`h-full rounded-full ${barTone(v.atingimento)}`} style={{ width: `${Math.min(100, v.atingimento)}%` }} />
                </div>
                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  <div><div className="text-sm font-semibold text-navy">{num(v.total_oportunidades)}</div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Oportunidades</div></div>
                  <div><div className="text-sm font-semibold text-brand">{brl(v.previsao_ponderada)}</div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Prev. ponderada</div></div>
                  <div><div className="text-sm font-semibold text-navy">{brl(v.total_valor)}</div><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Em aberto</div></div>
                </div>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-black/5 text-xs">
                  <span className="text-ok font-medium">{num(v.ganhas)} ganhas</span>
                  <span className="text-bad font-medium">{num(v.perdidas)} perdidas</span>
                  <span className={`font-medium ${v.requer_atencao > 0 ? "text-warn" : "text-slate-500"}`}>{num(v.requer_atencao)} atenção</span>
                  <Variacao v={v.variacao_realizado} novo={v.variacao_realizado_novo} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
